import { IWorkflowStoreState } from '@/store/workflow';
import { IWorkflowGetters } from './getters.types';
import {
  IWorkflow,
  Workflow,
  IWorkflowState,
  WorkflowState,
  WorkflowTransition,
  IWorkflowTransition,
  WorkflowStateThreshold,
  IWorkflowStateThreshold,
  WorkflowStateEnum,
  WorkflowStateTypeEnum,
  WorkflowStateAssignmentTypeEnum,
  WorkflowTransitionAction,
  IWorkflowTransitionAction,
  WorkflowTransitionActionTypeEnum
} from '@/models';
import { IState, State } from '@/models/state/state';

export const getters: IWorkflowGetters = {
  getWorkflows(state: IWorkflowStoreState): IWorkflow[] {
    return state.workflows.map(data => new Workflow(data));
  },

  getPublishedWorkflows(state: IWorkflowStoreState): IWorkflow[] {
    return state.workflows
      .map(data => new Workflow(data))
      .filter(workflow => workflow.isPublished());
  },

  getWorkflowById: (state: IWorkflowStoreState) => (
    id: number
  ): IWorkflow | undefined => {
    const data = state.workflows.find(workflow => workflow.id === id);
    if (!data) {
      return undefined;
    }

    return new Workflow(data);
  },

  /** #### STATE #### */
  getWorkflowStates(state: IWorkflowStoreState): IWorkflowState[] {
    return state.states.map(state => new WorkflowState(state));
  },

  getWorkflowStateById: (state: IWorkflowStoreState) => (
    stateId: number
  ): IWorkflowState | undefined => {
    const workflowState = state.states.find(state => state.id === stateId);
    if (workflowState) {
      return new WorkflowState(workflowState);
    }

    return undefined;
  },

  getLastUpsertedWorkflowStateId(state: IWorkflowStoreState): null | number {
    return state.lastUpsertedWorkflowStateId;
  },

  /** #### TRANSITION #### */
  getWorkflowTransitions(state: IWorkflowStoreState): IWorkflowTransition[] {
    return state.transitions.map(
      transition => new WorkflowTransition(transition)
    );
  },

  getWorkflowTransitionById: (state: IWorkflowStoreState) => (
    stateId: number
  ): IWorkflowTransition | undefined => {
    const workflowTransition = state.transitions.find(
      transition => transition.id === stateId
    );
    if (workflowTransition) {
      return new WorkflowTransition(workflowTransition);
    }

    return undefined;
  },

  getWorkflowStateVisualByState: (state: IWorkflowStoreState) => (
    workflowState: WorkflowStateEnum
  ): IState<WorkflowStateEnum> | undefined => {
    const stateData = state.workflowStates.find(
      stateVisual => stateVisual.value == workflowState
    );

    if (stateData) {
      return new State(stateData);
    }

    return undefined;
  },

  getWorkflowStateTypeVisuals: (
    state: IWorkflowStoreState
  ): IState<WorkflowStateTypeEnum>[] => {
    return state.workflowStateTypes.map(visual => new State(visual));
  },

  getWorkflowStateAssignmentTypeVisuals: (
    state: IWorkflowStoreState
  ): IState<WorkflowStateAssignmentTypeEnum>[] => {
    return state.workflowStateAssignmentTypes.map(visual => new State(visual));
  },

  getWorkflowStateAssignmentTypeVisualById: (state: IWorkflowStoreState) => (
    assignmentTypeId: number
  ): IState<WorkflowStateAssignmentTypeEnum> | undefined => {
    const visual = state.workflowStateAssignmentTypes.find(
      visual => visual.value === assignmentTypeId
    );
    return visual ? new State(visual) : undefined;
  },

  /** #### STATE THRESHOLD #### */
  getWorkflowStateThresholds(
    state: IWorkflowStoreState
  ): IWorkflowStateThreshold[] {
    return state.thresholds.map(
      threshold => new WorkflowStateThreshold(threshold)
    );
  },

  getWorkflowStateThresholdById: (state: IWorkflowStoreState) => (
    thresholdId: number
  ): IWorkflowStateThreshold | undefined => {
    const threshold = state.thresholds.find(t => t.id === thresholdId);
    if (threshold) {
      return new WorkflowStateThreshold(threshold);
    }

    return undefined;
  },

  getWorkflowTransitionActions: (
    state: IWorkflowStoreState
  ): IWorkflowTransitionAction[] => {
    return state.transitionActions.map(
      action => new WorkflowTransitionAction(action)
    );
  },

  getWorkflowTransitionActionById: (state: IWorkflowStoreState) => (
    actionId: number
  ): IWorkflowTransitionAction | undefined => {
    const action = state.transitionActions.find(
      action => action.id === actionId
    );
    if (action) {
      return new WorkflowTransitionAction(action);
    }

    return undefined;
  },

  getWorkflowTransitionActionTypeVisuals: (
    state: IWorkflowStoreState
  ): IState<WorkflowTransitionActionTypeEnum>[] => {
    return state.workflowTransitionActionTypes.map(visual => new State(visual));
  },

  getWorkflowTransitionActionTypeVisualByType: (state: IWorkflowStoreState) => (
    actionTypeId: number
  ): IState<WorkflowTransitionActionTypeEnum> | undefined => {
    const visual = state.workflowTransitionActionTypes.find(
      visual => visual.value === actionTypeId
    );
    return visual ? new State(visual) : undefined;
  }
};
